import { useState } from "react";
import { toast } from "sonner";
import { Upload, CheckCircle2, XCircle, FileText } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cardsApi } from "@/lib/api";

export default function ImportDialog({ open, onOpenChange, onDone, clients = [] }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [assignTo, setAssignTo] = useState("none");

  const reset = () => {
    setFile(null); setPreview(null); setResult(null);
  };

  const handleFile = async (f) => {
    if (!f) return;
    setFile(f);
    setLoading(true);
    setPreview(null);
    setResult(null);
    try {
      const data = await cardsApi.parseImport(f);
      setPreview(data);
    } catch (e) {
      toast.error("Failed to parse file");
    } finally {
      setLoading(false);
    }
  };

  const commit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const okRows = preview.rows.filter((r) => r.ok).map((r) => r.data);
      const r = await cardsApi.commitImport(okRows, assignTo === "none" ? null : assignTo);
      setResult(r);
      toast.success(`Imported ${r.imported}, skipped ${r.skipped}`);
      onDone?.();
    } catch {
      toast.error("Import failed");
    } finally {
      setCommitting(false);
    }
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-[#101B28] border-[#1E2D40] text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Import cards</DialogTitle>
        </DialogHeader>

        {!preview && !result && (
          <div className="py-6">
            <label
              className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-[#1E2D40] rounded-xl p-10 cursor-pointer hover:border-[#18BFFF]/50 transition-colors"
              data-testid="import-dropzone"
            >
              <Upload className="w-10 h-10 text-[#18BFFF]" />
              <div className="text-center">
                <div className="font-medium">Choose Excel (.xlsx) or CSV file</div>
                <div className="text-xs text-[#94A3B8] mt-1">
                  Required columns: full_name, email, company, slug
                </div>
              </div>
              <input
                type="file"
                accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="import-file-input"
              />
            </label>
            {loading && <p className="text-center text-[#94A3B8] mt-4">Parsing...</p>}
          </div>
        )}

        {preview && !result && (
          <div className="space-y-4">
            <div className="flex items-center gap-4 text-sm">
              <FileText className="w-4 h-4 text-[#18BFFF]" />
              <span className="text-white">{file?.name}</span>
              <span className="text-[#94A3B8]">·</span>
              <span className="text-[#94A3B8]">{preview.total} rows</span>
              <span className="text-[#94A3B8]">·</span>
              <span className="text-[#32D74B]">{preview.ok_count} valid</span>
              <span className="text-[#94A3B8]">·</span>
              <span className="text-[#FF453A]">{preview.total - preview.ok_count} errors</span>
            </div>
            <div className="border border-[#1E2D40] rounded-lg overflow-hidden max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0B131E] sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 text-[#94A3B8] font-medium">Row</th>
                    <th className="text-left px-3 py-2 text-[#94A3B8] font-medium">Name</th>
                    <th className="text-left px-3 py-2 text-[#94A3B8] font-medium">Slug</th>
                    <th className="text-left px-3 py-2 text-[#94A3B8] font-medium">Email</th>
                    <th className="text-left px-3 py-2 text-[#94A3B8] font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.row} className="border-t border-[#1E2D40]" data-testid={`preview-row-${r.row}`}>
                      <td className="px-3 py-2 text-[#94A3B8]">{r.row}</td>
                      <td className="px-3 py-2">{r.data.full_name || "—"}</td>
                      <td className="px-3 py-2"><code className="text-[#18BFFF] text-xs">{r.data.slug || "—"}</code></td>
                      <td className="px-3 py-2 text-[#94A3B8]">{r.data.email || "—"}</td>
                      <td className="px-3 py-2">
                        {r.ok ? (
                          <span className="inline-flex items-center gap-1 text-[#32D74B]"><CheckCircle2 className="w-3.5 h-3.5" /> OK</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[#FF453A]" title={r.errors.join(", ")}>
                            <XCircle className="w-3.5 h-3.5" /> {r.errors.join(", ")}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <div className="mr-auto flex items-center gap-2 text-xs text-[#94A3B8]">
                <label htmlFor="assignTo">Assign imported to:</label>
                <select id="assignTo" value={assignTo} onChange={(e) => setAssignTo(e.target.value)}
                        data-testid="import-assign-client"
                        className="bg-[#0B131E] border border-[#1E2D40] rounded-md px-2 py-1 text-white">
                  <option value="none">— None —</option>
                  {clients.map((cl) => <option key={cl.user_id} value={cl.user_id}>{cl.name || cl.email}</option>)}
                </select>
              </div>
              <Button variant="outline" onClick={reset} data-testid="import-restart"
                      className="border-[#1E2D40] bg-transparent text-white hover:bg-[#162436] hover:text-white">
                Choose different file
              </Button>
              <Button
                onClick={commit}
                disabled={committing || preview.ok_count === 0}
                data-testid="import-commit-btn"
                className="bg-[#18BFFF] text-[#07111D] hover:bg-[#4DD0FF]"
              >
                {committing ? "Importing..." : `Import ${preview.ok_count} card${preview.ok_count === 1 ? "" : "s"}`}
              </Button>
            </DialogFooter>
          </div>
        )}

        {result && (
          <div className="space-y-4 py-4" data-testid="import-result">
            <div className="rounded-xl border border-[#1E2D40] bg-[#0B131E] p-5">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A73]">Imported</div>
                  <div className="font-display text-3xl text-[#32D74B]">{result.imported}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A73]">Skipped</div>
                  <div className="font-display text-3xl text-[#FFD60A]">{result.skipped}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A73]">Errors</div>
                  <div className="font-display text-3xl text-[#FF453A]">{result.errors.length}</div>
                </div>
              </div>
            </div>
            {result.errors.length > 0 && (
              <div className="text-sm space-y-1 max-h-40 overflow-y-auto">
                {result.errors.map((e, i) => (
                  <div key={`${e.slug}-${i}`} className="text-[#94A3B8]">
                    <code className="text-[#FF453A]">{e.slug}</code>: {e.reason}
                  </div>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button onClick={close} data-testid="import-close"
                      className="bg-[#18BFFF] text-[#07111D] hover:bg-[#4DD0FF]">Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
